import React, { useMemo } from 'react';
import { Phone, MessageCircle, Mail, MapPin, Clock, CheckCircle, XCircle, AlertCircle, ArrowLeft, Star, School } from 'lucide-react';

// Mock theme hook pour la démo
const useTheme = () => ({
  theme: {
    primary: '#667eea',
    background: '#f8fafc',
    surface: '#ffffff',
    text: '#1f2937',
    textSecondary: '#6b7280',
    border: '#e5e7eb'
  }
});

export default function CentreDetailScreen({ route, navigation }) {
  // Mock data pour la démo
  const announcement = {
    titre: "Préparation aux Concours d'Ingénieur",
    description: "Formation complète et intensive pour réussir les concours d'entrée aux grandes écoles d'ingénieurs. Nos méthodes pédagogiques éprouvées et notre équipe d'enseignants expérimentés vous accompagnent vers la réussite.",
    imageUrl: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=400&fit=crop",
    concours: ["Polytechnique", "Centrale", "Mines", "ENAC"],
    etat: "approved",
    centerInfo: {
      centerName: "Excellence Academy",
      centerAddress: "123 Avenue des Sciences, Yaoundé"
    },
    contact: {
      telephone: "+237123456789",
      whatsapp: "+237123456789",
      email: "contact@excellence-academy.com"
    },
    avantages: [
      {
        titre: "Enseignants Qualifiés",
        description: "Professeurs diplômés des meilleures écoles avec une expérience pédagogique confirmée"
      },
      {
        titre: "Suivi Personnalisé",
        description: "Accompagnement individuel pour identifier et combler les lacunes de chaque étudiant"
      },
      {
        titre: "Ressources Complètes",
        description: "Accès à une bibliothèque numérique et physique avec tous les supports nécessaires"
      }
    ],
    subscriptionPlan: {
      expirationDate: {
        toDate: () => new Date(Date.now() + 10 * 24 * 60 * 60 * 1000) // 10 jours
      }
    }
  };

  const { theme } = useTheme();

  // Calcul du temps restant pour l'annonce
  const subscriptionStatus = useMemo(() => {
    if (!announcement.subscriptionPlan?.expirationDate) {
      return null;
    }
  
    const now = new Date();
    const expirationDate = announcement.subscriptionPlan.expirationDate.toDate 
      ? announcement.subscriptionPlan.expirationDate.toDate() 
      : new Date(announcement.subscriptionPlan.expirationDate);
      
    const timeRemaining = expirationDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeRemaining / (1000 * 3600 * 24));
  
    return {
      isExpired: timeRemaining <= 0,
      daysRemaining: Math.max(0, daysRemaining),
      expirationDate
    };
  }, [announcement]);

  const handleContact = (type, value) => {
    switch(type) {
      case 'phone':
        window.open(`tel:${value}`);
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${value.replace('+', '')}`);
        break;
      case 'email':
        window.open(`mailto:${value}`);
        break;
    }
  };

  const handleRenew = () => {
    // Mock navigation
    console.log('Navigate to RenewAnnouncementScreen');
  };

  const renderSubscriptionBanner = () => {
    if (announcement.etat !== 'approved') {
      return null;
    }
  
    if (!subscriptionStatus) return null;
  
    if (subscriptionStatus.isExpired) {
      return (
        <div className="subscription-banner expired">
          <AlertCircle size={24} className="subscription-icon" />
          <div className="subscription-content">
            <div className="subscription-text expired-text">
              Cette annonce a expiré
            </div>
            <button 
              className="renew-button"
              onClick={handleRenew}
            >
              Renouveler l'annonce
            </button>
          </div>
        </div>
      );
    }
  
    return (
      <div className="subscription-banner active">
        <Clock size={24} className="subscription-icon" />
        <div className="subscription-content">
          <div className="subscription-text active-text">
            {subscriptionStatus.daysRemaining <= 1 
              ? 'Expire demain'
              : `Expire dans ${subscriptionStatus.daysRemaining} jours`}
          </div>
          <div className="expiration-date">
            Date d'expiration: {subscriptionStatus.expirationDate.toLocaleDateString('fr-FR')}
          </div>
          {subscriptionStatus.daysRemaining <= 5 && (
            <button 
              className="renew-button"
              onClick={handleRenew}
            >
              Renouveler maintenant
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderStatusBanner = () => {
    if (announcement.etat === 'approved') {
      return (
        <div className="status-banner approved">
          <CheckCircle size={24} className="status-icon" />
          <div className="status-text">
            Cette annonce a été approuvée
          </div>
        </div>
      );
    } else if (announcement.etat === 'rejected') {
      return (
        <div className="status-banner rejected">
          <XCircle size={24} className="status-icon" />
          <div>
            <div className="status-text">
              Cette annonce a été refusée
            </div>
            {announcement.rejectionReason && (
              <div className="rejection-reason">
                Raison : {announcement.rejectionReason}
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div className="status-banner pending">
          <Clock size={24} className="status-icon" />
          <div className="status-text">
            Cette annonce est en attente d'approbation
          </div>
        </div>
      );
    }
  };

  return (
    <div className="safe-area">
      <style jsx>{`
        .safe-area {
          min-height: 100vh;
          background-color: ${theme.primary};
          display: flex;
          flex-direction: column;
        }

        .container {
          flex: 1;
          background-color: ${theme.background};
          overflow-y: auto;
          max-height: 100vh;
        }

        .header {
          height: 250px;
          position: relative;
          overflow: hidden;
        }

        .header-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .back-button {
          position: absolute;
          top: 20px;
          left: 20px;
          padding: 8px;
          border-radius: 50%;
          background-color: rgba(0, 0, 0, 0.5);
          border: none;
          cursor: pointer;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.3s;
          z-index: 10;
        }

        .back-button:hover {
          background-color: rgba(0, 0, 0, 0.7);
        }

        .content {
          flex: 1;
          background-color: ${theme.background};
          border-radius: 30px 30px 0 0;
          margin-top: -30px;
          padding: 0 20px 40px;
          position: relative;
          z-index: 5;
        }

        .centre-name {
          font-size: 24px;
          font-weight: bold;
          color: ${theme.text};
          margin-top: 20px;
          margin-bottom: 5px;
        }

        .location {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
          color: ${theme.textSecondary};
        }

        .location-text {
          margin-left: 8px;
        }

        .section {
          margin-bottom: 25px;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: ${theme.text};
          margin-bottom: 10px;
        }

        .description {
          color: ${theme.textSecondary};
          line-height: 1.6;
          margin-bottom: 15px;
        }

        .specialites-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 5px;
        }

        .specialite-badge {
          display: flex;
          align-items: center;
          background-color: ${theme.primary}25;
          padding: 6px 12px;
          border-radius: 12px;
          color: ${theme.primary};
          font-weight: 500;
        }

        .specialite-badge svg {
          margin-right: 6px;
        }

        .avantage-card {
          background-color: ${theme.surface};
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 10px;
          display: flex;
          align-items: flex-start;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .avantage-icon {
          background-color: ${theme.primary}25;
          padding: 10px;
          border-radius: 12px;
          margin-right: 12px;
          color: ${theme.primary};
        }

        .avantage-content {
          flex: 1;
        }

        .avantage-titre {
          font-size: 16px;
          font-weight: 600;
          color: ${theme.text};
          margin-bottom: 4px;
        }

        .avantage-description {
          color: ${theme.textSecondary};
          font-size: 14px;
          line-height: 1.5;
        }

        .contact-container {
          background-color: ${theme.surface};
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 30px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .contact-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: ${theme.primary};
          color: white;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 10px;
          border: none;
          cursor: pointer;
          font-size: 15px;
          font-weight: 600;
          transition: background-color 0.3s;
          width: 100%;
        }

        .contact-button:hover {
          background-color: ${theme.primary}dd;
        }

        .contact-button svg {
          margin-right: 8px;
        }

        .contact-button.whatsapp {
          background-color: #25D366;
        }

        .contact-button.whatsapp:hover {
          background-color: #128C7E;
        }

        .contact-button.email {
          background-color: #EA4335;
        }

        .contact-button.email:hover {
          background-color: #D33B2C;
        }

        .adresse {
          display: flex;
          align-items: flex-start;
          margin-top: 15px;
          color: ${theme.textSecondary};
        }

        .adresse-text {
          margin-left: 8px;
          flex: 1;
          line-height: 1.4;
        }

        .subscription-banner {
          padding: 15px;
          margin: 15px 0 20px;
          border-radius: 12px;
          display: flex;
          align-items: flex-start;
        }

        .subscription-banner.active {
          background-color: rgba(76, 175, 80, 0.2);
        }

        .subscription-banner.expired {
          background-color: rgba(255, 82, 82, 0.2);
        }

        .subscription-icon {
          margin-right: 10px;
          flex-shrink: 0;
        }

        .subscription-banner.active .subscription-icon {
          color: #4CAF50;
        }

        .subscription-banner.expired .subscription-icon {
          color: #FF5252;
        }

        .subscription-content {
          flex: 1;
        }

        .subscription-text {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .subscription-text.active-text {
          color: #4CAF50;
        }

        .subscription-text.expired-text {
          color: #FF5252;
        }

        .expiration-date {
          font-size: 14px;
          color: #666;
          margin-bottom: 8px;
        }

        .renew-button {
          background-color: #2196F3;
          color: white;
          padding: 8px 12px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          margin-top: 8px;
          transition: background-color 0.3s;
        }

        .renew-button:hover {
          background-color: #1976D2;
        }

        .status-banner {
          padding: 15px;
          margin: 15px 0 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .status-banner.approved {
          background-color: rgba(76, 175, 80, 0.2);
          color: #4CAF50;
        }

        .status-banner.rejected {
          background-color: rgba(255, 82, 82, 0.2);
          color: #FF5252;
        }

        .status-banner.pending {
          background-color: rgba(255, 193, 7, 0.2);
          color: #FFC107;
        }

        .status-icon {
          margin-right: 10px;
        }

        .status-text {
          font-size: 14px;
        }

        .rejection-reason {
          margin-top: 8px;
          color: ${theme.textSecondary};
        }

        @media (max-width: 768px) {
          .content {
            padding: 0 15px 40px;
          }
          
          .centre-name {
            font-size: 20px;
          }
          
          .section-title {
            font-size: 16px;
          }
        }
      `}</style>
      
      <div className="container">
        <div className="header">
          <img
            src={announcement.imageUrl}
            alt={announcement.titre}
            className="header-image"
          />
          <div
            className="back-button"
            onClick={() => navigation.goBack()}
          >
            <ArrowLeft size={24} />
          </div>
        </div>

        <div className="content">
          {renderSubscriptionBanner()}
          {renderStatusBanner()}

          <h1 className="centre-name">{announcement.titre}</h1>
          
          <div className="location">
            <MapPin size={18} />
            <span className="location-text">{announcement.centerInfo.centerAddress}</span>
          </div>

          <div className="section">
            <h2 className="section-title">Description</h2>
            <p className="description">{announcement.description}</p>
          </div>

          <div className="section">
            <h2 className="section-title">Concours préparés</h2>
            <div className="specialites-container">
              {announcement.concours.map((concours, index) => (
                <div key={index} className="specialite-badge">
                  <School size={16} />
                  {concours}
                </div>
              ))}
            </div>
          </div>

          {announcement.avantages?.length > 0 && (
            <div className="section">
              <h2 className="section-title">Pourquoi nous choisir ?</h2>
              {announcement.avantages.map((avantage, index) => (
                <div key={index} className="avantage-card">
                  <div className="avantage-icon">
                    <Star size={24} />
                  </div>
                  <div className="avantage-content">
                    <h3 className="avantage-titre">{avantage.titre}</h3>
                    <p className="avantage-description">{avantage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="section">
            <h2 className="section-title">Contact</h2>
            <div className="contact-container">
              <button 
                className="contact-button"
                onClick={() => handleContact('phone', announcement.contact.telephone)}
              >
                <Phone size={20} />
                Appeler
              </button>

              <button 
                className="contact-button whatsapp"
                onClick={() => handleContact('whatsapp', announcement.contact.whatsapp)}
              >
                <MessageCircle size={20} />
                WhatsApp
              </button>

              <button 
                className="contact-button email"
                onClick={() => handleContact('email', announcement.contact.email)}
              >
                <Mail size={20} />
                Email
              </button>

              <div className="adresse">
                <Phone size={20} />
                <span className="adresse-text">
                  {announcement.contact.telephone}
                </span>
              </div>

              <div className="adresse">
                <Mail size={20} />
                <span className="adresse-text">
                  {announcement.contact.email}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}